import { useCallback, useState, type ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { GameIframeModal } from '@/components/lobby/GameIframeModal.js';
import { mapApiError } from '@/lib/api/error.js';
import { launchGame, PopupBlockedError, type LaunchOutcome } from './launchGame.js';
import type { Game, LaunchMethod } from './api.js';

interface ModalState {
  url: string;
  method: LaunchMethod;
  payload: Record<string, unknown> | null;
  title?: string;
  /** Kept so the frame can explain a promotion that forbids this camp (#546). */
  game: LaunchableGame;
}

interface UseGameLaunchOptions {
  /** Language code passed to the provider — usually the active locale. */
  lang: string;
}

/**
 * What a caller must know about a game to launch it. `gameType` and the provider
 * labels are not needed to launch — they are what the open frame is judged by
 * when a promotion restricts play (#546), and every catalog card carries them.
 */
export type LaunchableGame = Pick<
  Game,
  'id' | 'providerId' | 'name' | 'launchMode' | 'launchMethod'
> &
  Partial<Pick<Game, 'code' | 'gameType' | 'providerCode' | 'providerDisplayName'>>;

export interface UseGameLaunchResult {
  /** Wrap your click handler with this. Resolves once launch dispatches. */
  launch: (game: LaunchableGame) => Promise<LaunchOutcome | null>;
  /** Render this in your component tree — null when no iframe game is active. */
  modal: ReactNode;
}

/**
 * One-call abstraction for launching a game from any list/card. Owns the
 * iframe-modal state, surfaces toast errors (including popup-blocked with a
 * manual-open action), and forwards the per-game mode hint from the catalog
 * so the popup pre-open happens synchronously on click.
 */
export function useGameLaunch(opts: UseGameLaunchOptions): UseGameLaunchResult {
  const { lang } = opts;
  const { t } = useTranslation(['lobby', 'common', 'games']);
  const navigate = useNavigate();
  const [modalState, setModalState] = useState<ModalState | null>(null);

  const launch = useCallback(
    async (game: LaunchableGame) => {
      if (!game.providerId) return null;
      try {
        const outcome = await launchGame({
          providerId: game.providerId,
          gameId: game.id,
          lang,
          hint:
            game.launchMode && game.launchMethod
              ? { mode: game.launchMode, method: game.launchMethod }
              : null,
        });
        if (outcome.kind === 'internal') {
          await navigate({
            to: '/games/originals/$code',
            params: { code: outcome.code },
            search: { session: outcome.session },
          });
          return outcome;
        }
        if (outcome.kind === 'iframe') {
          setModalState({
            url: outcome.url,
            method: outcome.method,
            payload: outcome.payload,
            title: game.name,
            game,
          });
        }
        return outcome;
      } catch (err) {
        if (err instanceof PopupBlockedError) {
          const url = err.url;
          toast.error(t('games:popupBlocked.title'), {
            action: {
              label: t('games:popupBlocked.openManually'),
              onClick: () => window.location.assign(url),
            },
            duration: 10_000,
          });
        } else {
          toast.error(mapApiError(err, t));
        }
        return null;
      }
    },
    [lang, t, navigate],
  );

  const modal = modalState ? (
    <GameIframeModal
      url={modalState.url}
      method={modalState.method}
      payload={modalState.payload}
      title={modalState.title}
      game={modalState.game}
      onClose={() => setModalState(null)}
    />
  ) : null;

  return { launch, modal };
}
